import React from 'react';
import Icon from '../../../components/AppIcon';

const TestimonialSection = () => {
  const testimonials = [
    {
      id: 1,
      name: "Sarah Chen",
      role: "Software Engineer → Senior Developer",
      company: "Tech Corp",
      avatar: "/assets/images/male_mechanic.png",
      content: `CareerLift's AI-generated plan helped me identify the exact skills I needed to advance. I got promoted to Senior Developer within 6 months of following their recommendations.`,
      rating: 5,
      highlight: "Promoted in 6 months"
    },
    {
      id: 2,
      name: "Marcus Johnson",
      role: "Marketing Coordinator → Marketing Manager",
      company: "Growth Agency",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      content: `The weekly exercises and progress tracking kept me accountable. The personalized approach made all the difference in my career development journey.`,
      rating: 5,
      highlight: "40% salary increase"
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      role: "Registered Nurse → Nurse Practitioner",
      company: "Metro Health",
      avatar: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=150&h=150&fit=crop&crop=face",
      content: `The role-specific assessments understood exactly what I needed for healthcare advancement. Now I'm pursuing my NP degree with confidence.`,
      rating: 5,
      highlight: "Clear career path"
    }
  ];

  const stats = [
    {
      value: "10,000+",
      label: "Professionals Advanced",
      icon: "TrendingUp"
    },
    {
      value: "85%",
      label: "Promotion Rate",
      icon: "Award"
    },
    {
      value: "4.9/5",
      label: "User Rating",
      icon: "Star"
    },
    {
      value: "6 months",
      label: "Average Time to Promotion",
      icon: "Clock"
    }
  ];

  return (
    <div className="space-y-8">
      {/* Success Stats */}
      <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl p-6">
        <div className="text-center mb-6">
          <h3 className="text-xl font-semibold text-foreground mb-2">
            Join Thousands of Successful Professionals
          </h3>
          <p className="text-muted-foreground">
            Real results from real people advancing their careers
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats?.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-2">
                <Icon name={stat?.icon} size={24} className="text-primary" />
              </div>
              <div className="text-2xl font-bold text-foreground">{stat?.value}</div>
              <div className="text-sm text-muted-foreground">{stat?.label}</div>
            </div>
          ))}
        </div>
      </div>
      {/* Testimonials */}
      <div>
        <div className="text-center mb-2">
          <h3 className="text-xl font-semibold text-foreground mb-2">
            Success Stories
          </h3>
          <p className="text-muted-foreground">
            See how CareerLift Premium transformed these careers
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {testimonials?.map((testimonial) => (
            <div key={testimonial?.id} className="bg-card rounded-lg border border-border p-6 hover:shadow-md transition-shadow duration-200">
              {/* Rating */}
              <div className="flex items-center space-x-1 mb-4">
                {Array.from({ length: testimonial?.rating }, (_, i) => (
                  <Icon key={i} name="Star" size={16} className="text-warning fill-current" />
                ))}
              </div>

              {/* Content */}
              <blockquote className="text-muted-foreground text-sm mb-4 leading-relaxed">
                "{testimonial?.content}"
              </blockquote>

              {/* Author */}
              <div className="flex items-center space-x-3">
                <img
                  src={testimonial?.avatar}
                  alt={testimonial?.name}
                  className="w-10 h-10 rounded-full object-cover"
                  onError={(e) => {
                    e.target.src = '/assets/images/no_image.png';
                  }}
                />
                <div className="flex-1">
                  <div className="font-medium text-foreground text-sm">{testimonial?.name}</div>
                  <div className="text-xs text-muted-foreground">{testimonial?.role}</div>
                  <div className="text-xs text-muted-foreground">{testimonial?.company}</div>
                </div>
                {testimonial?.highlight && (
                  <div className="text-right">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-success/10 text-success">
                      <Icon name="TrendingUp" size={10} className="mr-1" />
                      {testimonial?.highlight}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Trust Signals */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="text-center mb-6">
          <h4 className="font-semibold text-foreground mb-2">Trusted by Professionals Worldwide</h4>
          <p className="text-muted-foreground text-sm">
            Your career development is backed by industry expertise and security
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Icon name="Shield" size={24} className="text-success" />
            </div>
            <h5 className="font-medium text-foreground mb-1">Secure & Private</h5>
            <p className="text-xs text-muted-foreground">
              Your career data is encrypted and never shared with employers
            </p>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Icon name="Award" size={24} className="text-primary" />
            </div>
            <h5 className="font-medium text-foreground mb-1">Expert-Backed</h5>
            <p className="text-xs text-muted-foreground">
              Developed with career coaches and industry professionals
            </p>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Icon name="RefreshCw" size={24} className="text-warning" />
            </div>
            <h5 className="font-medium text-foreground mb-1">Money-Back Guarantee</h5>
            <p className="text-xs text-muted-foreground">
              30-day full refund if you're not completely satisfied
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestimonialSection;